/* eslint-disable react-hooks/exhaustive-deps */
import {
  Badge,
  Button,
  Drawer,
  Fab,
  IconButton,
  Link,
  Popover,
  Stack,
  styled,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Box } from '@mui/system';
import { INotification } from 'src/models/general';
import { CHECK_NOTIFICATION } from 'src/services/graphql/notification/query';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useAppSelector } from 'src/app/hooks';
import ItemActivity from './ItemActivity';
import ButtonCircle from 'src/components/ButtonCircle';
import {
  DELETE_ALL_NOTIFICATION,
  DELETE_NOTIFICATION
} from 'src/services/graphql/notification/mutation';
import CircularProgress from '@mui/material/CircularProgress';
import TooltipCustomize from 'src/components/TooltipCustomize';
import { useDispatch } from 'react-redux';
import { downloadState, downloadBtnState } from 'src/features/download';
import CloseIcon from '@mui/icons-material/Close';
import { FirstTimeTour } from 'src/constants/productTour/FirstTimeTour';
import ProductTourTooltip from 'src/components/ProductTourTooltip';
import { dummyNotification } from 'src/constants/dummies';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import useRefreshObjectList from 'src/hooks/useRefreshObjectList';
import {
  reducerUpdateOnTour,
  reducerUpdateTourSteps
} from 'src/features/productTour';
import Empty from 'src/components/Empty';
import { useAlert } from 'src/hooks/useAlert';
import { SETTINGS_OBJECT } from 'src/route';
import { useNavigate } from 'react-router';

const FabCustom = styled(Fab)(
  ({ theme }) => `
      background: linear-gradient(90deg, #DDF5FF 0%, #C1FEFA 50.65%, #C1ECFE 100%);
      opacity: 0.7;
      border: 1px solid rgba(0, 0, 0, 0.15);
      color: ${theme.palette.primary.main};
  `
);

const OBJECT_ACTIVITY_TYPES = new Set([
  'firstTimeSocialMediaData',
  'copyKeyword'
]);

interface IActivityProps {
  variant?: 'fab' | 'circle';
}

const Activity: React.FC<IActivityProps> = ({ variant = 'fab' }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { handleClickAlert } = useAlert();
  const navigate = useNavigate();
  const projectId = useAppSelector((state) => state.users.project.id);
  const [anchor, setAnchor] = useState<boolean>(false);
  const [progresses, setProgresses] = useState<Array<INotification>>([]);
  const [isLoadingProgresses, setIsLoadingProgresses] = useState<boolean>(true);
  const [activity, setActivity] = useState({
    isInvisible: false,
    activityLength: 0
  });
  const showDownload = useAppSelector((state) => state.downloadState.isShow);
  const drawerRef = useRef(null);
  const { customerType } = useAppSelector((state) => state.users.project);

  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const id = open ? 'simple-popover' : undefined;

  const {
    isOnTour,
    currentTour: { stepNumber, menu }
  } = useAppSelector((state) => state.storeProductTour);

  const refreshObjectList = useRefreshObjectList();

  const [getNotification] = useLazyQuery(CHECK_NOTIFICATION, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (response) => {
      if (!response) return;
      const currentProgresses: INotification[] =
        response.notification_getNotification;

      setProgresses(currentProgresses);
      setIsLoadingProgresses(false);

      if (customerType !== 'b2c') return;

      let shouldRefresh = false;

      // for first time when activity list is empty
      if (!progresses.length) {
        shouldRefresh = currentProgresses.some(
          (activity) =>
            activity.currentProgress > 0 &&
            activity.status === 'error' &&
            OBJECT_ACTIVITY_TYPES.has(activity.subTypeNotification)
        );
      } else {
        const progressMap = new Map(progresses.map((p) => [p.id, p]));

        // compare previous and current progresses
        for (const activity of currentProgresses) {
          const isObjectActivity = OBJECT_ACTIVITY_TYPES.has(
            activity.subTypeNotification
          );
          if (!isObjectActivity) continue;

          const prevActivity = progressMap.get(activity.id);
          if (!prevActivity) continue;

          const becomeError =
            prevActivity.status !== 'error' && activity.status === 'error';
          const becomeProcessing =
            prevActivity.currentProgress === 0 && activity.currentProgress > 0;

          if (becomeError) {
            handleClickAlert({
              severity: 'error',
              message:
                'Failed to add object. Please delete the object with error status and ',
              action: (
                <Link
                  variant="inherit"
                  component={'button'}
                  sx={{ color: '#662531' }}
                  onClick={() => navigate(SETTINGS_OBJECT)}
                >
                  try adding it again &gt;
                </Link>
              )
            });
          }
          if (becomeError || becomeProcessing) {
            shouldRefresh = true;
            break;
          }
        }
      }

      if (shouldRefresh) refreshObjectList();
    },
    onError: () => {
      setIsLoadingProgresses(false);
    }
  });

  useEffect(() => {
    const timer = setInterval(() => {
      getNotification({
        variables: { projectId }
      });
    }, 10 * 1000);
    return () => {
      clearInterval(timer);
    };
  }, [projectId, getNotification]);

  const [deleteNotification, resDeleteNotification] =
    useMutation(DELETE_NOTIFICATION);
  const handleDelete = function (id: string) {
    deleteNotification({
      variables: {
        notificationId: id
      }
    }).then(({ data }) => {
      setProgresses((prevProgresses) =>
        prevProgresses.filter(
          (progress) => progress.id !== data.notification_deleteNotification.id
        )
      );
    });
  };

  const [deleteAllNotification, resDeleteAllNotification] = useMutation(
    DELETE_ALL_NOTIFICATION
  );
  const handleDeleteAll = function () {
    deleteAllNotification({
      variables: {
        projectId
      }
    }).then(() => {
      setProgresses(() => []);
      dispatch(downloadBtnState(true));
    });
  };

  useEffect(() => {
    const statusActivity = progresses.filter(
      (status) => status.status === 'pending'
    );
    setActivity({ isInvisible: false, activityLength: statusActivity.length });
  }, [progresses]);

  useEffect(() => {
    const statusDownload = progresses.filter(
      (status) =>
        status.status === 'pending' && status.typeNotification === 'download'
    );
    if (statusDownload.length > 0) {
      dispatch(downloadBtnState(false));
    } else {
      dispatch(downloadBtnState(true));
    }
  }, [activity]);

  const handleClickActivity = () => {
    setAnchor(true);
    setActivity({ ...activity, isInvisible: true });
    dispatch(downloadState(false));
  };

  const handleRetry = () => {
    getNotification({
      variables: { projectId }
    }).then(() => drawerRef.current.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const isCurrentlyOnTourFirstTimeStep3 =
    isOnTour && stepNumber === 3 && menu === FirstTimeTour.menu;
  const isCurrentlyOnTourFirstTimeStep4 =
    isOnTour && stepNumber === 4 && menu === FirstTimeTour.menu;

  const isFirstTime = Boolean(localStorage.getItem('isFirstTime'));

  const handleCloseActivityDrawer = () => {
    setAnchor(false);
    if (!isFirstTime || !matchesUpMd) return;
    localStorage.removeItem('isFirstTime');
  };

  useEffect(() => {
    if (!isFirstTime) return;
    handleClickActivity();
  }, [isFirstTime, progresses]);

  return (
    <>
      <ProductTourTooltip
        open={isCurrentlyOnTourFirstTimeStep3}
        placement="left-start"
      >
        <Stack
          direction={'row'}
          alignItems={'center'}
          spacing={theme.spacing(1.5)}
          sx={{
            ...(isCurrentlyOnTourFirstTimeStep3
              ? {
                  position: 'relative',
                  zIndex: 1000,
                  pointerEvents: 'none'
                }
              : {})
          }}
        >
          {showDownload ? (
            <>
              <Typography
                sx={{ mb: theme.spacing(0.5), fontWeight: 600 }}
                variant="subtitle2"
              >
                Downloading
              </Typography>

              <Box sx={{ display: 'flex' }}>
                <CircularProgress size={15} disableShrink />
              </Box>
            </>
          ) : null}
          <TooltipCustomize title="List activity" placement="top-start">
            {variant === 'fab' ? (
              <FabCustom
                size="small"
                sx={{
                  width: theme.spacing(4.4),
                  height: theme.spacing(4.4)
                }}
                onClick={handleClickActivity}
              >
                <Badge badgeContent={activity.activityLength} color="error">
                  <ListAltIcon sx={{ fontSize: theme.spacing(3) }} />
                </Badge>
              </FabCustom>
            ) : (
              <ButtonCircle
                sx={{ background: '#F5F5F5' }}
                onClick={handleClickActivity}
              >
                <Badge badgeContent={activity.activityLength} color="error">
                  <Tooltip arrow title="List Activity">
                    <ListAltIcon sx={{ color: '#285771' }} />
                  </Tooltip>
                </Badge>
              </ButtonCircle>
            )}
          </TooltipCustomize>
        </Stack>
      </ProductTourTooltip>
      <Drawer
        anchor="right"
        open={isCurrentlyOnTourFirstTimeStep4 || anchor}
        onClose={handleCloseActivityDrawer}
        PaperProps={{ ref: drawerRef }}
        sx={isOnTour ? { pointerEvents: 'none' } : {}}
      >
        <Box
          sx={{
            width: theme.spacing(45.5),
            p: theme.spacing(2),
            background: theme.palette.grey[100]
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            {!matchesUpMd && (
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => handleCloseActivityDrawer()}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="h3"
                sx={{ fontWeight: 400, fontSize: '18px', color: '#4B4B4B' }}
              >
                Activity
              </Typography>
              <Stack onClick={handleClick} sx={{ cursor: 'pointer' }}>
                <MoreVertIcon />
              </Stack>
            </Stack>
            {matchesUpMd && (
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => handleCloseActivityDrawer()}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        </Box>
        <ProductTourTooltip
          open={isCurrentlyOnTourFirstTimeStep4}
          onClick={() => setAnchor(false)}
        >
          <Box sx={{ px: theme.spacing(1) }}>
            {isLoadingProgresses ? (
              <Box
                sx={{
                  height: '500px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <CircularProgress />
              </Box>
            ) : progresses.length === 0 ? (
              <Box
                sx={{
                  height: '500px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Empty message="No data available" />
              </Box>
            ) : (
              <ItemActivity
                progresses={isOnTour ? dummyNotification : progresses}
                onDelete={handleDelete}
                loading={resDeleteNotification.loading}
                onClose={() => setAnchor(false)}
                onRetry={handleRetry}
              />
            )}
          </Box>
        </ProductTourTooltip>
      </Drawer>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Button
          variant="text"
          endIcon={<ClearAllIcon />}
          disabled={resDeleteAllNotification.loading}
          onClick={handleDeleteAll}
          sx={{ borderRadius: 0 }}
        >
          Clear All
        </Button>
      </Popover>
    </>
  );
};

export default Activity;
